import { useState, useRef, useEffect, useCallback } from 'react';
import type { Product } from '../types/product';
import type { CartItem, CartContextType, ServerCartItem } from '../types/cart';
import { cartService } from '../services/cartService';
import { getProductById } from '../services/productService';
import { useTranslation } from 'react-i18next';
import { tokenStorage } from '../utils/tokenStorage';
import { useToast } from './ToastContext';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { CACHE_DURATIONS } from '../utils/cacheManager';
import config from '../config/config.json';
import { useAuth } from '../hooks/useAuth';
import { useAnalytics } from '../hooks/useAnalytics';
import logger from '../utils/logger';
import { CartContext } from './CartContextDefinition';

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartBouncing, setIsCartBouncing] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [processingItems, setProcessingItems] = useState<Set<string>>(new Set());
  const cartButtonRef = useRef<HTMLButtonElement>(null);
  const { i18n } = useTranslation();
  const { showToast } = useToast();
  const { isOnline } = useNetworkStatus();
  const { isAuthenticated } = useAuth();
  const { trackAddToCart, trackRemoveFromCart } = useAnalytics();

  // Helper function to check if any cart operation is in progress
  const isCartOperationInProgress = () => {
    return processingItems.size > 0;
  };

  // Helper function to check if a specific item is being processed
  const isItemBeingProcessed = (itemId: string) => {
    return processingItems.has(itemId);
  };

  // Helper function to add item to processing
  const addProcessingItem = (itemId: string) => {
    setProcessingItems((prev) => new Set(prev).add(itemId));
  };

  // Helper function to remove item from processing
  const removeProcessingItem = (itemId: string) => {
    setProcessingItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };

  // Helper function to check network and show appropriate message
  const checkNetworkConnection = () => {
    if (!isOnline) {
      const errorMessage =
        i18n.language === 'mm'
          ? 'ကွန်ယက်ချိတ်ဆက်မှု ပြတ်တောက်နေပါသည်။ ကျေးဇူးပြု၍ သင့်အင်တာနက်ချိတ်ဆက်မှုကို စစ်ဆေးပါ။'
          : 'Network connection lost. Please check your internet connection.';
      showToast('error', errorMessage);
      return false;
    }
    return true;
  };

  // Track pending add to cart operations to prevent duplicates
  const pendingAddToCartRef = useRef<Set<string>>(new Set());

  const loadCart = useCallback(async () => {
    try {
      // Load cart for both authenticated and guest users
      // The cartService will handle authentication vs guest session automatically

      // Check if we have cached product details in localStorage with expiration
      const cachedProducts = localStorage.getItem('cart_product_details');
      const cacheTimestamp = localStorage.getItem('cart_cache_timestamp');

      let productCache = {};

      // Only use cache if it's not expired
      if (cachedProducts && cacheTimestamp) {
        const now = Date.now();
        const cacheTime = parseInt(cacheTimestamp);

        if (now - cacheTime < CACHE_DURATIONS.LONG) {
          productCache = JSON.parse(cachedProducts);
        } else {
          // Clear expired cache
          localStorage.removeItem('cart_product_details');
          localStorage.removeItem('cart_cache_timestamp');
        }
      }

      const response = await cartService.getCart();
      logger.log('🔍 CartContext loadCart - Server response:', response);

      if (response.success && response.data.items) {
        logger.log('🔍 CartContext loadCart - Server items:', response.data.items);
        // Convert server cart items to local cart items format
        const localItems: CartItem[] = await Promise.all(
          response.data.items.map(async (item) => {
            let productName = 'Product';
            let productImage = '/placeholder-image.jpg';
            let productPrice = item.price;

            // Always prioritize server data over cache
            // Try to use cart item data (which comes from server) first
            if (item.name && typeof item.name === 'object') {
              const currentLang = i18n.language as 'en' | 'my';
              const nameObj = item.name as Record<string, string>;
              productName = nameObj[currentLang] || nameObj.my || nameObj.en || 'Product';
            } else if (item.name && typeof item.name === 'string') {
              productName = item.name;
            } else if (
              (item as unknown as { _nameMultilingual?: Record<string, string> })._nameMultilingual
            ) {
              const currentLang = i18n.language as 'en' | 'my';
              const nameMultilingual = (
                item as unknown as { _nameMultilingual: Record<string, string> }
              )._nameMultilingual;
              productName =
                nameMultilingual[currentLang] ||
                nameMultilingual.my ||
                nameMultilingual.en ||
                'Product';
            }

            if (item.image) {
              // Clean the image URL by removing extra spaces, backticks, and quotes
              productImage = item.image.trim().replace(/^[`"'\s]+|[`"'\s]+$/g, '');
              // Validate if it's a proper URL
              if (
                productImage &&
                (productImage.startsWith('http') || productImage.startsWith('//'))
              ) {
                // Keep the cleaned URL
              } else {
                productImage = '/placeholder-image.jpg';
              }
            }

            const cachedProductDetail = (
              productCache as Record<string, { name: string; image: string; price: number }>
            )[item.productId];

            // Only use cache as fallback if server data is incomplete
            if (productName === 'Product' && cachedProductDetail?.name) {
              productName = cachedProductDetail.name;
            }

            if (productImage === '/placeholder-image.jpg' && cachedProductDetail?.image) {
              productImage = cachedProductDetail.image;
            }

            const hasUsableCachedData = Boolean(
              (cachedProductDetail?.name && cachedProductDetail.name !== 'Product') ||
                (cachedProductDetail?.image &&
                  cachedProductDetail.image !== '/placeholder-image.jpg'),
            );
            const needsProductLookup =
              productName === 'Product' || productImage === '/placeholder-image.jpg' || !item.image;

            // Avoid repeated product fetches if cache already has usable fallback data.
            if (needsProductLookup && !hasUsableCachedData) {
              try {
                const product = await getProductById(item.productId);
                if (product) {
                  if (
                    (product as Product & { _nameMultilingual?: Record<string, string> })
                      ._nameMultilingual
                  ) {
                    const currentLang = i18n.language as 'en' | 'my';
                    const nameMultilingual = (
                      product as Product & { _nameMultilingual: Record<string, string> }
                    )._nameMultilingual;
                    productName =
                      nameMultilingual[currentLang] ||
                      nameMultilingual.my ||
                      nameMultilingual.en ||
                      'Product';
                  }

                  if (product.image) {
                    // Clean the image URL by removing extra spaces, backticks, and quotes
                    productImage = product.image.trim().replace(/^[`"'\s]+|[`"'\s]+$/g, '');
                    // Validate if it's a proper URL
                    if (
                      productImage &&
                      (productImage.startsWith('http') || productImage.startsWith('//'))
                    ) {
                      // Keep the cleaned URL
                    } else {
                      productImage = '/placeholder-image.jpg';
                    }
                  }

                  if (product.price) {
                    productPrice = product.price;
                  }

                  // Cache the product details with timestamp
                  (productCache as Record<string, { name: string; image: string; price: number }>)[
                    item.productId
                  ] = {
                    name: productName,
                    image: productImage,
                    price: productPrice,
                  };
                  localStorage.setItem('cart_product_details', JSON.stringify(productCache));
                  localStorage.setItem('cart_cache_timestamp', Date.now().toString());
                }
              } catch (productError) {
                logger.warn('Failed to fetch product details for cart item:', productError);
              }
            }

            return {
              id: item.productId,
              title: productName,
              name: productName,
              price: productPrice,
              discountPrice: (item as unknown as ServerCartItem).discountPrice,
              quantity: item.quantity,
              image: productImage,
              type: (item as unknown as CartItem).type,
              fx: (item as unknown as CartItem).fx,
              currencySymbol: (item as unknown as CartItem).currencySymbol,
              discountAmount: (item as unknown as ServerCartItem).discountAmount,
              discountType: (item as unknown as ServerCartItem).discountType,
              discountValue: (item as unknown as ServerCartItem).discountValue,
              commissionRate: (item as unknown as ServerCartItem).commissionRate,
              flashSaleId: (item as unknown as ServerCartItem).flashSaleId,
              flashUnitPrice: (item as unknown as ServerCartItem).flashUnitPrice,
              flashQuantity: (item as unknown as ServerCartItem).flashQuantity,
              flashSaleMaxQty: (item as unknown as ServerCartItem).flashSaleMaxQty,
              flashSaleWindowKey: (item as unknown as ServerCartItem).flashSaleWindowKey,
            };
          }),
        );

        // Remove duplicates based on product ID
        const uniqueItems = localItems.reduce((acc, item) => {
          const existingIndex = acc.findIndex((existing) => existing.id === item.id);
          if (existingIndex >= 0) {
            acc[existingIndex].quantity += item.quantity;
          } else {
            acc.push(item);
          }
          return acc;
        }, [] as CartItem[]);

        logger.log('🔍 CartContext loadCart - Final uniqueItems:', uniqueItems);
        setItems(uniqueItems);
        logger.log('🔍 CartContext loadCart - Items state updated');
      }
    } catch (error) {
      logger.error('Failed to load cart:', error);
    }
  }, [i18n.language]);

  // Lazy load cart - only load when user interacts with cart or after initial page load
  const [hasLoadedCart, setHasLoadedCart] = useState(false);

  const lazyLoadCart = useCallback(async () => {
    if (!hasLoadedCart) {
      await loadCart();
      setHasLoadedCart(true);
    }
  }, [loadCart, hasLoadedCart]);

  // Only load cart when user is on pages that need cart functionality
  // This prevents unnecessary API calls on pages like SharePage, OrderDetailPage, etc.
  useEffect(() => {
    const timer = setTimeout(() => {
      lazyLoadCart();
    }, 1000); // Delay cart loading by 1 second

    return () => clearTimeout(timer);
  }, [lazyLoadCart]);

  // Reload cart when authentication state changes (login/logout)
  useEffect(() => {
    if (hasLoadedCart) {
      loadCart();
    }
  }, [isAuthenticated, loadCart, hasLoadedCart]);

  // Listen for role switch events to refresh cart data
  useEffect(() => {
    const handleRoleSwitch = (event: CustomEvent) => {
      logger.log('🔄 Role switch detected in CartContext, refreshing cart data...', event.detail);
      if (hasLoadedCart) {
        clearCache();
        loadCart();
      }
    };

    // Listen for the custom roleSwitch event dispatched from AuthProvider
    window.addEventListener('roleSwitch', handleRoleSwitch as EventListener);

    return () => {
      window.removeEventListener('roleSwitch', handleRoleSwitch as EventListener);
    };
  }, [loadCart, hasLoadedCart]);

  const addToCart = async (product: Product, quantity: number, retryCount = 0) => {
    const maxRetries = 2;
    const operationKey = `${product.id}_${quantity}`;

    // Check if this exact operation is already pending
    if (pendingAddToCartRef.current.has(operationKey)) {
      logger.log('Add to cart operation already pending for:', operationKey);
      return;
    }

    // Check network connection before proceeding
    if (!checkNetworkConnection()) {
      return;
    }

    try {
      // Add item to processing set
      addProcessingItem(product.id);

      // Set global adding state only if no other items are being processed
      if (processingItems.size === 0) {
        setIsAddingToCart(true);
      }

      // Only load cart if it hasn't been loaded yet
      if (!hasLoadedCart) {
        await lazyLoadCart();
      }

      // Add to cart for both authenticated and guest users
      // The cartService will handle authentication vs guest session automatically

      const response = await cartService.addToCart({
        productId: product.id,
        quantity: quantity,
      });

      // Show success toast
      if (response.message) {
        showToast('success', response.message);
      }

      // Reload cart from server after successful API call
      await loadCart();

      // Track add to cart event
      trackAddToCart({
        item_id: product.id,
        item_name: product.title || product.name || 'Unknown Product',
        category:
          typeof product.categoryId === 'object'
            ? product.categoryId.name
            : product.category || 'Unknown',
        quantity: quantity,
        price: product.price,
      });

      // Trigger bounce animation after cart is updated
      setIsCartBouncing(true);
      setTimeout(() => setIsCartBouncing(false), 300);
    } catch (error) {
      logger.error('Failed to add to cart:', error);

      // Handle different types of errors
      let errorMessage =
        i18n.language === 'mm'
          ? 'ပစ္စည်းကို cart ထဲသို့ ထည့်ရန် မအောင်မြင်ပါ'
          : 'Failed to add item to cart';
      let shouldRetry = false;

      if (error instanceof Error) {
        // Network errors
        if (error.message.includes('fetch') || error.message.includes('network')) {
          errorMessage =
            i18n.language === 'mm'
              ? 'ကွန်ယက်အမှား။ ကျေးဇူးပြု၍ သင့်ချိတ်ဆက်မှုကို စစ်ဆေးပါ။'
              : 'Network error. Please check your connection.';
          shouldRetry = retryCount < maxRetries;
        }
        // Server errors
        else if (error.message.includes('500') || error.message.includes('server')) {
          errorMessage =
            i18n.language === 'mm'
              ? 'ဆာဗာအမှား။ ကျေးဇူးပြု၍ ပြန်လည်ကြိုးစားပါ။'
              : 'Server error. Please try again.';
          shouldRetry = retryCount < maxRetries;
        }
        // Custom error messages from API
        else if (error.message) {
          errorMessage = error.message;
        }
      }

      // Retry logic for network/server errors
      if (shouldRetry) {
        logger.log(`Retrying add to cart (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(
          () => {
            addToCart(product, quantity, retryCount + 1);
          },
          1000 * (retryCount + 1),
        ); // Exponential backoff
        return;
      }

      showToast('error', errorMessage);
    } finally {
      // Remove item from processing set
      removeProcessingItem(product.id);

      // Reset global adding state only if no items are being processed
      if (processingItems.size <= 1) {
        // <= 1 because we haven't removed from set yet
        setIsAddingToCart(false);
      }
    }
  };

  const removeFromCart = async (productId: string, retryCount = 0) => {
    const maxRetries = 2;

    // Check network connection before proceeding
    if (!checkNetworkConnection()) {
      return;
    }

    try {
      // Add item to processing set
      addProcessingItem(productId);

      // Remove from cart for both authenticated and guest users
      // The cartService will handle authentication vs guest session automatically

      // Find the item being removed for analytics tracking
      const itemToRemove = items.find((item) => item.id === productId);

      const response = await cartService.removeFromCart(productId);

      // Track remove from cart event
      if (itemToRemove) {
        trackRemoveFromCart({
          item_id: itemToRemove.id,
          item_name: itemToRemove.title || 'Unknown Product',
          category: 'Unknown',
          quantity: itemToRemove.quantity,
          price: itemToRemove.price,
        });
      }

      // Only update UI after successful API response
      setItems(items.filter((item) => item.id !== productId));

      // Show success toast
      if (response.message) {
        showToast('success', response.message);
      }
    } catch (error) {
      logger.error('Failed to remove from cart:', error);

      // Handle different types of errors
      let errorMessage =
        i18n.language === 'mm'
          ? 'ပစ္စည်းကို cart မှ ဖယ်ရှားရန် မအောင်မြင်ပါ'
          : 'Failed to remove item from cart';
      let shouldRetry = false;

      if (error instanceof Error) {
        // Network errors
        if (error.message.includes('fetch') || error.message.includes('network')) {
          errorMessage =
            i18n.language === 'mm'
              ? 'ကွန်ယက်အမှား။ ကျေးဇူးပြု၍ သင့်ချိတ်ဆက်မှုကို စစ်ဆေးပါ။'
              : 'Network error. Please check your connection.';
          shouldRetry = retryCount < maxRetries;
        }
        // Server errors
        else if (error.message.includes('500') || error.message.includes('server')) {
          errorMessage =
            i18n.language === 'mm'
              ? 'ဆာဗာအမှား။ ကျေးဇူးပြု၍ ပြန်လည်ကြိုးစားပါ။'
              : 'Server error. Please try again.';
          shouldRetry = retryCount < maxRetries;
        }
        // Custom error messages from API
        else if (error.message) {
          errorMessage = error.message;
        }
      }

      // Retry logic for network/server errors
      if (shouldRetry) {
        logger.log(`Retrying remove from cart (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(
          () => {
            removeFromCart(productId, retryCount + 1);
          },
          1000 * (retryCount + 1),
        ); // Exponential backoff
        return;
      }

      showToast('error', errorMessage);
    } finally {
      // Remove item from processing set
      removeProcessingItem(productId);
    }
  };

  const updateQuantity = async (productId: string, quantity: number, retryCount = 0) => {
    const maxRetries = 2;

    // Validate quantity against config limits
    if (quantity < config.cart.minQuantityPerItem || quantity > config.cart.maxQuantityPerItem) {
      const errorMessage =
        i18n.language === 'mm'
          ? `အရေအတွက်သည် ${config.cart.minQuantityPerItem} နှင့် ${config.cart.maxQuantityPerItem} ကြားတွင် ရှိရမည်`
          : `Quantity must be between ${config.cart.minQuantityPerItem} and ${config.cart.maxQuantityPerItem}`;
      showToast('error', errorMessage);
      return;
    }

    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    // Check network connection before proceeding
    if (!checkNetworkConnection()) {
      return;
    }

    try {
      // Add item to processing set
      addProcessingItem(productId);

      // Update cart quantity for both authenticated and guest users
      // The cartService will handle authentication vs guest session automatically

      await cartService.updateCartItem({
        productId: productId,
        quantity: quantity,
      });

      // Only update UI after successful API response
      setItems(items.map((item) => (item.id === productId ? { ...item, quantity } : item)));
    } catch (error) {
      logger.error('Failed to update quantity:', error);

      // Handle different types of errors
      let errorMessage =
        i18n.language === 'mm'
          ? 'ပစ္စည်းအရေအတွက် ပြောင်းလဲရန် မအောင်မြင်ပါ'
          : 'Failed to update item quantity';
      let shouldRetry = false;

      if (error instanceof Error) {
        // Network errors
        if (error.message.includes('fetch') || error.message.includes('network')) {
          errorMessage =
            i18n.language === 'mm'
              ? 'ကွန်ယက်အမှား။ ကျေးဇူးပြု၍ သင့်ချိတ်ဆက်မှုကို စစ်ဆေးပါ။'
              : 'Network error. Please check your connection.';
          shouldRetry = retryCount < maxRetries;
        }
        // Server errors
        else if (error.message.includes('500') || error.message.includes('server')) {
          errorMessage =
            i18n.language === 'mm'
              ? 'ဆာဗာအမှား။ ကျေးဇူးပြု၍ ပြန်လည်ကြိုးစားပါ။'
              : 'Server error. Please try again.';
          shouldRetry = retryCount < maxRetries;
        }
        // Custom error messages from API
        else if (error.message) {
          errorMessage = error.message;
        }
      }

      // Retry logic for network/server errors
      if (shouldRetry) {
        logger.log(`Retrying update quantity (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(
          () => {
            updateQuantity(productId, quantity, retryCount + 1);
          },
          1000 * (retryCount + 1),
        ); // Exponential backoff
        return;
      }

      showToast('error', errorMessage);
    } finally {
      // Remove item from processing set
      removeProcessingItem(productId);
    }
  };

  const clearCart = async (retryCount = 0) => {
    const maxRetries = 2;

    // Check network connection before proceeding
    if (!checkNetworkConnection()) {
      return;
    }

    try {
      // Clear cart for both authenticated and guest users
      // The cartService will handle authentication vs guest session automatically

      const response = await cartService.clearCart();
      setItems([]);
      localStorage.removeItem('cart_product_details');

      // Show success toast
      if (response.message) {
        showToast('success', response.message);
      }
    } catch (error) {
      logger.error('Failed to clear cart:', error);

      // Handle different types of errors
      let errorMessage =
        i18n.language === 'mm' ? 'Cart ကို ရှင်းလင်းရန် မအောင်မြင်ပါ' : 'Failed to clear cart';
      let shouldRetry = false;

      if (error instanceof Error) {
        // Network errors
        if (error.message.includes('fetch') || error.message.includes('network')) {
          errorMessage =
            i18n.language === 'mm'
              ? 'ကွန်ယက်အမှား။ ကျေးဇူးပြု၍ သင့်ချိတ်ဆက်မှုကို စစ်ဆေးပါ။'
              : 'Network error. Please check your connection.';
          shouldRetry = retryCount < maxRetries;
        }
        // Server errors
        else if (error.message.includes('500') || error.message.includes('server')) {
          errorMessage =
            i18n.language === 'mm'
              ? 'ဆာဗာအမှား။ ကျေးဇူးပြု၍ ပြန်လည်ကြိုးစားပါ။'
              : 'Server error. Please try again.';
          shouldRetry = retryCount < maxRetries;
        }
        // Custom error messages from API
        else if (error.message) {
          errorMessage = error.message;
        }
      }

      // Retry logic for network/server errors
      if (shouldRetry) {
        logger.log(`Retrying clear cart (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(
          () => {
            clearCart(retryCount + 1);
          },
          1000 * (retryCount + 1),
        ); // Exponential backoff
        return;
      }

      showToast('error', errorMessage);
    }
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => {
      const itemPrice = item.discountPrice || item.price || 0;
      return total + itemPrice * item.quantity;
    }, 0);
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const clearCache = () => {
    localStorage.removeItem('cart_product_details');
    localStorage.removeItem('cart_cache_timestamp');
  };

  const refreshCart = async () => {
    setIsRefreshing(true);
    try {
      clearCache();
      await loadCart();
    } finally {
      setIsRefreshing(false);
    }
  };

  const checkout = async () => {
    try {
      // Check if user is authenticated
      const token = tokenStorage.getAccessToken();
      if (!token) {
        throw new Error('User not authenticated');
      }

      // Check if cart is not empty
      if (items.length === 0) {
        throw new Error('Cart is empty');
      }

      // Call checkout initialize API
      const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/checkout/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language === 'mm' ? 'mm' : 'en',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            productId: item.id,
            quantity: item.quantity,
            price: item.price,
          })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Checkout initialization failed');
      }

      const data = await response.json();

      // Show success toast
      if (data.message) {
        showToast('success', data.message);
      }

      return data;
    } catch (error) {
      logger.error('Checkout failed:', error);
      if (error instanceof Error && error.message) {
        showToast('error', error.message);
      }
      throw error;
    }
  };

  const value: CartContextType = {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    refreshCart,
    getTotalPrice,
    getTotalItems,
    isCartBouncing,
    isAddingToCart,
    isRefreshing,
    cartButtonRef,
    clearCache,
    isCartOperationInProgress,
    isItemBeingProcessed,
    checkout,
    loadCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
